import type { ChangeEvent } from 'react';
import { useMemo, useState } from 'react';
import { debounce } from 'lodash-es';
import { proxy, useSnapshot } from 'valtio';
import { subscribeKey } from 'valtio/utils';
import { cleanTextSearchInput } from '@soundxyz/utils';
import { SEARCH_TAB_CHOICES } from '@components/Explore/schema';
import { setActiveTab } from '@components/Explore/useSearchPageStore';
import { ParamsStore } from '@context/params';
import type { SearchInput } from '@graphql/reactQuery';
import { useStableCallback } from '@hooks/useStableCallback';

const SEARCH_RESULT_LIMIT = 5;

export type GlobalSearchState = {
  barInputText: string;
  barSearchText: string;
  searchPageText: string;
  focus: boolean;
};

const searchSound: GlobalSearchState = {
  barInputText: ParamsStore.q ?? '',
  barSearchText: ParamsStore.q ?? '',
  searchPageText: ParamsStore.q ?? '',
  focus: false,
};

type UseSearchInputProps = { limit?: number; searchState: GlobalSearchState };

/*
 * Keep track of search input text
 * So we can access it from anywhere in the app
 */
export const globalSearchSoundState: GlobalSearchState = proxy(searchSound);

export function setSearchPageText({ text }: { text: string }) {
  ParamsStore.q = text;
  globalSearchSoundState.searchPageText = text;
}

// This clears the search input when navigation happens
subscribeKey(ParamsStore, 'q', () => {
  if (!ParamsStore.q) {
    globalSearchSoundState.barInputText = '';
    globalSearchSoundState.searchPageText = '';

    if (!!ParamsStore.tab) {
      ParamsStore.tab = SEARCH_TAB_CHOICES.SOUNDS;
      setActiveTab({ activeTab: SEARCH_TAB_CHOICES.SOUNDS });
    }
  }
});

subscribeKey(globalSearchSoundState, 'searchPageText', () => {
  // If search input has contents (user searched), we want to show ALL tab
  // If not and current tab is ALL, we want to show Sounds tab
  if (globalSearchSoundState.searchPageText) {
    setActiveTab({ activeTab: SEARCH_TAB_CHOICES.ALL });
  } else {
    setActiveTab({ activeTab: SEARCH_TAB_CHOICES.SOUNDS });
  }
});

export function useSearchInputNew(props?: UseSearchInputProps) {
  const { limit = SEARCH_RESULT_LIMIT, searchState = globalSearchSoundState } = props || {};
  const { searchPageText, barInputText, focus, barSearchText } = useSnapshot(searchState, {
    sync: true,
  });

  const [hasHadFocus, setHasHadFocus] = useState<boolean>(false);

  const debounceSetText = useMemo(() => {
    return debounce(
      (eventTextValue: string) => {
        const cleanTextValue = cleanTextSearchInput(eventTextValue);
        if (!cleanTextValue) {
          searchState.barSearchText = '';
        } else {
          searchState.barSearchText = cleanTextValue;
        }
      },
      300,
      {
        trailing: true,
        leading: false,
      },
    );
  }, [searchState]);

  const input = useMemo<SearchInput>(() => {
    return {
      text: barInputText,
      limit,
    };
  }, [limit, barInputText]);

  const handleInputChange = useStableCallback((ev: ChangeEvent<HTMLInputElement>) => {
    searchState.barInputText = ev.target.value;
    debounceSetText(ev.target.value);
  });

  const isEmptyInputText = !searchState.searchPageText?.length;

  const clearText = useStableCallback(() => {
    searchState.barInputText = '';
    searchState.barSearchText = '';
    searchState.searchPageText = '';
    ParamsStore.q = null;

    if (ParamsStore.tab === SEARCH_TAB_CHOICES.ALL || ParamsStore.tab === null) {
      ParamsStore.tab = SEARCH_TAB_CHOICES.SOUNDS;
      setActiveTab({ activeTab: SEARCH_TAB_CHOICES.SOUNDS });
    }
  });

  const focusOn = useStableCallback(() => {
    setHasHadFocus(true);
    searchState.focus = true;
  });

  const focusOff = useStableCallback(() => {
    searchState.focus = false;
  });

  return {
    isEmptyInputText,
    input,
    barInputText,
    searchPageText,
    clearText,
    handleInputChange,
    focus,
    focusOn,
    focusOff,
    barSearchText,
    hasHadFocus,
  } as const;
}
